import React, {useEffect, useMemo, useRef} from "react";
import { useIntl } from "react-intl";
import MaterialTable from "@material-table/core";
import { getColumns, OPTIONS, getLocalization } from "./Params";
import { tableIcons } from "../../TableIcons";
import AuthenticationService from "../../../services/AuthenticationService";
import TableStyle from "../../../components/TableStyle";
import {STATUSES} from "@feature/users/lib/consts";
import {PRIVILEGED_ROLES} from "../../../constants";

const UsersTable = ({
  fetch,
  update,
  remove,
  loading,
  internalUsers,
  roles,
  refreshObservable,
}) => {

  // hooks
  const intl = useIntl();

  // utilities
  const scopes = AuthenticationService.getUserScopes();
  const rolesLkp = roles.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: intl.formatMessage({id: `user_type_${curr.name}`}),
    }),
    {},
  );
  const statusesLkp = STATUSES.reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: intl.formatMessage({ id: `user_status_${curr}`}),
    }),
    {},
  );
  const columns = useMemo(
    () => getColumns(intl, internalUsers, rolesLkp, statusesLkp),
    [internalUsers, roles]
  );
  const canRowBeUpdated = ({role: {name}}) => [...PRIVILEGED_ROLES, "field"].includes(name);

  // handlers
  const _onRowUpdate = (newData, oldData) =>
    new Promise(async (resolve, reject) => {
      const result = await update(newData, oldData);
      if (!!result) resolve(result)
      else reject();
    });
  const _onRowDelete = (newData) =>
    new Promise(async (resolve, reject) => {
      const result = await remove(newData.id);
      if (result !== null) resolve()
      else reject();
    });

  // refs
  const tableRef = useRef(null);

  useEffect(() => {
    tableRef.current?.onQueryChange(undefined);
  }, [refreshObservable]);  // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <TableStyle>
      <MaterialTable
        tableRef={tableRef}
        data={(query) => fetch(query, columns)}
        columns={columns}
        icons={tableIcons}
        options={OPTIONS}
        localization={getLocalization(intl)}
        isLoading={loading}
        editable={{
          ...(scopes.indexOf("user:update") >= 0 ? { onRowUpdate: _onRowUpdate } : {}),
          ...(scopes.indexOf("user:delete") >= 0 ? { onRowDelete: _onRowDelete } : {}),
          isEditHidden: (rowData) => !canRowBeUpdated(rowData),
          isDeleteHidden: (rowData) => !canRowBeUpdated(rowData),
        }}
      />
    </TableStyle>
  );
}

export default UsersTable;
